import { configureStore } from "@reduxjs/toolkit";
import productsSlice from "./slices/productsSlice";
import cartSlice from "./slices/cartSlice";
import userSlice from "./slices/userSlice";
import configSlice from "./slices/configSlice";

// Get user ID from state or userInfo
const getUserId = (state) => {
  return state?.user?.userInfo?.id || state?.user?.userInfo?._id || "guest";
};

// Function to get the storage key for a specific user
const getStorageKey = (userId) => {
  return `reduxState_${userId}`;
};

// Function to save selected slices to localStorage with user ID
const saveStateToLocalStorage = (state, slicesToPersist) => {
  const userId = getUserId(state);
  const stateToSave = {};

  slicesToPersist.forEach((slice) => {
    if (state[slice]) {
      stateToSave[slice] = state[slice];
    }
  });

  const storageKey = getStorageKey(userId);
  localStorage.setItem(storageKey, JSON.stringify(stateToSave));
};

// Function to load the persisted state from localStorage
const loadStateFromLocalStorage = () => {
  try {
    // Try to find the correct storage key based on existing persisted user data
    let storageKey = getStorageKey("guest");
    
    // Check all localStorage keys for user-specific Redux states
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("reduxState_") && key !== "reduxState_guest") {
        try {
          const tempState = JSON.parse(localStorage.getItem(key));
          // If this state has a valid token, use it
          if (tempState?.user?.token) {
            storageKey = key;
            break;
          }
        } catch (e) {
          // Skip invalid JSON
          continue;
        }
      }
    }

    const serializedState = localStorage.getItem(storageKey);
    if (serializedState) {
      return JSON.parse(serializedState);
    }
  } catch (e) {
    console.error("Error loading state from localStorage:", e);
  }
  return {};
};

// List of slices to persist
const slicesToPersist = ["cart", "user"];

const preloadedState = loadStateFromLocalStorage();

const store = configureStore({
  reducer: {
    product: productsSlice,
    cart: cartSlice,
    user: userSlice,
    config: configSlice,
  },
  preloadedState,
});

// Track the current user ID to detect changes
let currentUserId = getUserId(store.getState());

// Save state to localStorage on every state change
store.subscribe(() => {
  const state = store.getState();
  const newUserId = getUserId(state);

  // If user ID changed, save the new user's state
  if (newUserId !== currentUserId) {
    currentUserId = newUserId;
  }

  // Always save state to the current user's storage
  saveStateToLocalStorage(state, slicesToPersist);
});

export default store;
